import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  ClipboardCopy,
  FilePlus2,
  Plus,
  Send,
  Square,
  Undo2,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { PageLoader } from "@/components/ui/Spinner";
import { useEngAronMeta } from "../api/engAronApi";
import {
  useAddManualFinding,
  useCancelReview,
  useDecideReview,
  useNewVersionReview,
  usePatchFinding,
  usePromoteFinding,
  useReview,
  useReviewEmailText,
  useReviewFindings,
  useReviewReviewers,
  useShareReview,
  useStartReview,
  useTaskPolling,
} from "../api/reviewsApi";
import { REVIEW_MODES } from "../utils";
import {
  ReviewModeInfo,
  useReviewModeOptions,
} from "../components/ReviewModeInfo";
import type { EngReview, ReviewDecision } from "../types";
import {
  CATEGORY_LABEL,
  DOC_TYPE_LABEL,
  FINDING_STATUS_LABEL,
  REVIEW_STATUS_COLOR,
  REVIEW_STATUS_LABEL,
  reviewProgress,
  SEVERITY_COLOR,
  SEVERITY_LABEL,
  WORKFLOW_STATE_COLOR,
  WORKFLOW_STATE_LABEL,
} from "../utils";
import type { EngFinding } from "../types";

const SEVERITY_OPTIONS = (
  Object.keys(SEVERITY_LABEL) as (keyof typeof SEVERITY_LABEL)[]
).map((v) => ({ value: v, label: SEVERITY_LABEL[v] }));
const CATEGORY_OPTIONS = [
  { value: "", label: "— bez kategorii —" },
  ...Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label })),
];

const BRANCH_LABEL: Record<string, string> = {
  telecom: "Telekomunikacja",
  energy: "Energetyka",
};

function PromoteModal({
  finding,
  onClose,
}: {
  finding: EngFinding;
  onClose: () => void;
}) {
  const meta = useEngAronMeta();
  const promote = usePromoteFinding();
  const [kind, setKind] = useState("error");
  const [scope, setScope] = useState("global");
  const [title, setTitle] = useState(finding.body.slice(0, 120));
  const [body, setBody] = useState(finding.body);
  const [error, setError] = useState<string | null>(null);

  const isFalsePositive = kind === "false_positive";

  // Pełne poziomy: globalny zawsze; branża/inwestor/projekt gdy recenzja daje kontekst.
  const scopeOptions = [
    { value: "global", label: "Globalny" },
    ...(finding.review_branch
      ? [
          {
            value: "branch",
            label: `Branża (${BRANCH_LABEL[finding.review_branch] ?? finding.review_branch})`,
          },
        ]
      : []),
    ...(finding.review_investor
      ? [
          {
            value: "investor",
            label: `Inwestor (${finding.review_investor_name ?? "z projektu"})`,
          },
        ]
      : []),
    ...(finding.review_project
      ? [
          {
            value: "project",
            label: `Projekt (${finding.review_project_name ?? finding.review_label})`,
          },
        ]
      : []),
  ];

  async function submit() {
    setError(null);
    if (!body.trim()) {
      setError("Treść wpisu nie może być pusta.");
      return;
    }
    const scoped: Record<string, string> = {};
    if (scope === "branch" && finding.review_branch)
      scoped.branch = finding.review_branch;
    if (scope === "investor" && finding.review_investor)
      scoped.investor = finding.review_investor;
    if (scope === "project" && finding.review_project)
      scoped.project = finding.review_project;
    try {
      await promote.mutateAsync({
        id: finding.id,
        kind,
        scope,
        title: title.trim() || body.trim().slice(0, 120),
        body: body.trim(),
        ...scoped,
      });
      onClose();
    } catch {
      setError("Nie udało się utworzyć wpisu.");
    }
  }

  return (
    <Modal open onClose={onClose} title="Dodaj uwagę do bazy wiedzy" size="md">
      <div className="space-y-3">
        <p className="text-xs text-[var(--color-text-muted)] border-l-2 border-[var(--color-border-strong)] pl-2 whitespace-pre-wrap">
          Uwaga źródłowa: {finding.body}
        </p>
        <Select
          label="Rodzaj wpisu"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value);
            // Przy zmianie na fałszywy alarm podpowiedz szablon powodu.
            if (e.target.value === "false_positive" && body === finding.body) {
              setBody(`To NIE jest błąd: ${finding.body}\nPowód: `);
            }
          }}
          options={meta.data?.kinds ?? []}
        />
        <Input
          label={
            isFalsePositive ? "Tytuł (czego dotyczy fałszywy alarm)" : "Tytuł"
          }
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div>
          <label className="block text-xs text-[var(--color-text-muted)] mb-1">
            {isFalsePositive
              ? "Treść — opisz DLACZEGO to nie jest błąd (ENG-ARON przestanie to zgłaszać)"
              : "Treść (to dokładnie przeczyta ENG-ARON)"}
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            className="w-full border border-[var(--color-border-strong)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-primary)] outline-none"
          />
        </div>
        <Select
          label="Poziom (zakres obowiązywania)"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          options={scopeOptions}
        />
        <p className="text-xs text-[var(--color-text-muted)]">
          {isFalsePositive
            ? "Uwaga zostanie oznaczona jako odrzucona, a wpis trafi do bazy jako znany fałszywy alarm."
            : "Wpis możesz jeszcze dopracować w Bazie wiedzy (tytuł/treść/typy)."}{" "}
          {meta.data?.is_approver ? "" : "Trafi najpierw do akceptacji."}
        </p>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={submit} loading={promote.isPending}>
            {isFalsePositive ? "Oznacz jako fałszywy alarm" : "Dodaj do bazy"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function FindingCard({ finding }: { finding: EngFinding }) {
  const patch = usePatchFinding();
  const [promoteOpen, setPromoteOpen] = useState(false);
  const dismissed = finding.status === "dismissed";
  // Uwagi „poprawione" przeniesione z poprzedniej wersji — tylko do odczytu,
  // bez akcji potwierdź/odrzuć/do bazy wiedzy.
  const fixed = finding.status === "fixed";

  return (
    <div
      className={`border p-3 space-y-1.5 ${
        fixed
          ? "border-green-600/50 bg-[var(--color-bg-surface)]"
          : "border-[var(--color-border)] bg-[var(--color-bg-surface)]"
      } ${dismissed ? "opacity-50" : ""}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-bold text-[var(--color-text-primary)]">
            {finding.number}.
          </span>
          <Badge color={SEVERITY_COLOR[finding.severity]}>
            {SEVERITY_LABEL[finding.severity]}
          </Badge>
          {finding.category && (
            <Badge color="gray">
              {CATEGORY_LABEL[finding.category] ?? finding.category}
            </Badge>
          )}
          {finding.is_manual && (
            <Badge color="blue">
              ręczna
              {finding.created_by_name ? ` · ${finding.created_by_name}` : ""}
            </Badge>
          )}
          {finding.page_refs.length > 0 && (
            <span className="text-xs text-[var(--color-text-muted)]">
              s. {finding.page_refs.join(", ")}
            </span>
          )}
          {finding.kb_entry_title && (
            <span
              className="text-xs text-[var(--color-text-muted)]"
              title="Wpis bazy wiedzy"
            >
              ⚖ {finding.kb_entry_title}
            </span>
          )}
        </div>
        {fixed ? (
          <span className="text-xs font-medium text-green-600 inline-flex items-center gap-1">
            <CheckCircle2 size={12} /> Poprawiona
          </span>
        ) : (
          <span className="text-xs text-[var(--color-text-muted)]">
            {FINDING_STATUS_LABEL[finding.status]}
          </span>
        )}
      </div>

      <p
        className={`text-sm whitespace-pre-wrap ${dismissed ? "line-through" : ""} text-[var(--color-text-primary)]`}
      >
        {finding.body}
      </p>
      {finding.excerpt && (
        <p className="text-xs text-[var(--color-text-muted)] border-l-2 border-[var(--color-border-strong)] pl-2">
          „{finding.excerpt}”
        </p>
      )}

      {!fixed && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {finding.status !== "confirmed" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                patch.mutate({ id: finding.id, status: "confirmed" })
              }
            >
              <Check size={12} className="mr-1" /> Potwierdź
            </Button>
          )}
          {!dismissed ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                patch.mutate({ id: finding.id, status: "dismissed" })
              }
            >
              <X size={12} className="mr-1" /> Odrzuć
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => patch.mutate({ id: finding.id, status: "open" })}
            >
              <Square size={12} className="mr-1" /> Przywróć
            </Button>
          )}
          {!finding.promoted_entry ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPromoteOpen(true)}
            >
              → Do bazy wiedzy
            </Button>
          ) : (
            <span className="text-xs text-green-600 self-center">
              ✓ w bazie wiedzy
            </span>
          )}
        </div>
      )}

      {promoteOpen && (
        <PromoteModal finding={finding} onClose={() => setPromoteOpen(false)} />
      )}
    </div>
  );
}

function NewVersionModal({
  review,
  onClose,
}: {
  review: EngReview;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const newVersion = useNewVersionReview();
  const [files, setFiles] = useState<File[]>([]);
  const [mode, setMode] = useState(review.mode);
  const [skipAi, setSkipAi] = useState(false);
  const modeOptions = useReviewModeOptions();
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    if (files.length === 0) {
      setError("Dodaj poprawiony PDF.");
      return;
    }
    try {
      const next = await newVersion.mutateAsync({
        id: review.id,
        file: files[0],
        ...(skipAi ? { skip_ai: true } : { mode }),
      });
      onClose();
      navigate(`/eng-aron/reviews/${next.id}`);
    } catch (e: unknown) {
      const detail = (
        e as { response?: { data?: { errors?: { detail?: string } } } }
      )?.response?.data?.errors?.detail;
      setError(detail ?? "Nie udało się utworzyć nowej wersji.");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Nowa wersja (v${review.version + 1})`}
      size="md"
    >
      <div className="space-y-3">
        <p className="text-sm text-[var(--color-text-secondary)]">
          {skipAi ? (
            <>
              Wgraj poprawiony PDF. ENG-ARON <b>nie</b> będzie go ponownie
              analizować — nowa wersja będzie od razu gotowa do udostępnienia
              recenzentom.
            </>
          ) : (
            <>
              Wgraj poprawiony PDF. ENG-ARON sprawdzi go ponownie w pełni, ale z
              pamięcią tej wersji: <b>potwierdzone</b> uwagi sprawdzi, czy
              poprawiono, a <b>odrzucone</b> pominie (nie zgłosi ponownie).
            </>
          )}
        </p>
        <FileDropZone
          files={files}
          onFilesChange={setFiles}
          accept=".pdf,application/pdf"
        />
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={skipAi}
            onChange={(e) => setSkipAi(e.target.checked)}
            className="accent-[var(--color-primary)] mt-0.5"
          />
          <span className="text-sm text-[var(--color-text-primary)]">
            Bez analizy AI
            <span className="block text-xs text-[var(--color-text-muted)]">
              Gdy poprawki były drobne (np. literówka) i ponowne sprawdzanie nie
              ma sensu. Analizę można później dorobić przyciskiem „Analizuj”.
            </span>
          </span>
        </label>
        {!skipAi && (
          <>
            <Select
              label="Tryb analizy"
              value={mode}
              onChange={(e) => setMode(e.target.value as EngReview["mode"])}
              options={modeOptions}
            />
            <ReviewModeInfo mode={mode} />
          </>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={submit} loading={newVersion.isPending}>
            {skipAi ? "Wgraj bez analizy" : "Wgraj i sprawdź ponownie"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ShareModal({
  review,
  onClose,
}: {
  review: EngReview;
  onClose: () => void;
}) {
  const info = useReviewReviewers(review.id, true);
  const share = useShareReview();
  const [picked, setPicked] = useState<string[]>(
    review.assignments.map((a) => a.reviewer),
  );
  const [error, setError] = useState<string | null>(null);

  function toggle(id: string) {
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  async function submit() {
    setError(null);
    if (picked.length === 0) {
      setError("Wybierz co najmniej jednego recenzenta.");
      return;
    }
    try {
      await share.mutateAsync({ id: review.id, reviewer_ids: picked });
      onClose();
    } catch (e: unknown) {
      const detail = (
        e as { response?: { data?: { errors?: { detail?: string } } } }
      )?.response?.data?.errors?.detail;
      setError(detail ?? "Nie udało się udostępnić recenzji.");
    }
  }

  const assignable = info.data?.assignable ?? [];

  return (
    <Modal open onClose={onClose} title="Udostępnij recenzentom" size="md">
      <div className="space-y-3">
        <p className="text-sm text-[var(--color-text-secondary)]">
          Recenzentem może być wyłącznie dyrektor techniczny. Po udostępnieniu
          dostanie powiadomienie i będzie mógł dodać własne uwagi oraz podjąć
          decyzję.
        </p>
        {info.isLoading ? (
          <p className="text-sm text-[var(--color-text-muted)]">Ładowanie…</p>
        ) : assignable.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">
            Brak dostępnych dyrektorów technicznych.
          </p>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {assignable.map((u) => (
              <label
                key={u.id}
                className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-[var(--color-bg-elevated)]"
              >
                <input
                  type="checkbox"
                  checked={picked.includes(u.id)}
                  onChange={() => toggle(u.id)}
                  className="accent-[var(--color-primary)]"
                />
                <span className="text-sm text-[var(--color-text-primary)]">
                  {u.name}
                </span>
              </label>
            ))}
          </div>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            onClick={submit}
            loading={share.isPending}
            disabled={assignable.length === 0}
          >
            Udostępnij
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const DECISION_META: Record<
  ReviewDecision,
  {
    title: string;
    noteLabel: string;
    noteRequired: boolean;
    cta: string;
    hint: string;
  }
> = {
  accepted: {
    title: "Zaakceptuj opracowanie",
    noteLabel: "Komentarz (opcjonalnie)",
    noteRequired: false,
    cta: "Zaakceptuj",
    hint: "Opracowanie zostanie zaakceptowane bez uwag.",
  },
  accepted_conditional: {
    title: "Zaakceptuj warunkowo",
    noteLabel: "Warunki / uwagi do poprawy (wymagane)",
    noteRequired: true,
    cta: "Zaakceptuj warunkowo",
    hint: "Autor poprawia wg uwag, ale NIE musi odsyłać do ponownej recenzji.",
  },
  returned: {
    title: "Zakończ recenzję i zwróć do autora",
    noteLabel: "Co poprawić przed kolejną wersją (wymagane)",
    noteRequired: true,
    cta: "Zakończ i zwróć",
    hint: "Recenzja tej wersji zostaje zamknięta i wraca do autora — inni recenzenci nie będą jej już kontynuować.",
  },
};

function DecisionModal({
  review,
  decision,
  onClose,
}: {
  review: EngReview;
  decision: ReviewDecision;
  onClose: () => void;
}) {
  const decide = useDecideReview();
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const meta = DECISION_META[decision];

  async function submit() {
    setError(null);
    if (meta.noteRequired && !note.trim()) {
      setError("To pole jest wymagane.");
      return;
    }
    try {
      await decide.mutateAsync({ id: review.id, decision, note: note.trim() });
      onClose();
    } catch (e: unknown) {
      const detail = (
        e as { response?: { data?: { errors?: { detail?: string } } } }
      )?.response?.data?.errors?.detail;
      setError(detail ?? "Nie udało się zapisać decyzji.");
    }
  }

  return (
    <Modal open onClose={onClose} title={meta.title} size="md">
      <div className="space-y-3">
        <p className="text-sm text-[var(--color-text-secondary)]">
          {meta.hint}
        </p>
        <div>
          <label className="block text-xs text-[var(--color-text-muted)] mb-1">
            {meta.noteLabel}
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={5}
            className="w-full border border-[var(--color-border-strong)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-primary)] outline-none"
          />
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant={decision === "returned" ? "danger" : undefined}
            onClick={submit}
            loading={decide.isPending}
          >
            {meta.cta}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ManualFindingModal({
  review,
  onClose,
}: {
  review: EngReview;
  onClose: () => void;
}) {
  const add = useAddManualFinding();
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("major");
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    if (!body.trim()) {
      setError("Treść uwagi nie może być pusta.");
      return;
    }
    try {
      await add.mutateAsync({
        id: review.id,
        body: body.trim(),
        category,
        severity,
      });
      onClose();
    } catch (e: unknown) {
      const detail = (
        e as { response?: { data?: { errors?: { detail?: string } } } }
      )?.response?.data?.errors?.detail;
      setError(detail ?? "Nie udało się dodać uwagi.");
    }
  }

  return (
    <Modal open onClose={onClose} title="Dodaj uwagę ręcznie" size="md">
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-[var(--color-text-muted)] mb-1">
            Treść uwagi
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            autoFocus
            className="w-full border border-[var(--color-border-strong)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-primary)] outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Kategoria"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            options={CATEGORY_OPTIONS}
          />
          <Select
            label="Waga"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            options={SEVERITY_OPTIONS}
          />
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={submit} loading={add.isPending}>
            Dodaj uwagę
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function WorkflowPanel({ review }: { review: EngReview }) {
  const [shareOpen, setShareOpen] = useState(false);
  const [decision, setDecision] = useState<ReviewDecision | null>(null);
  // Udostępnić można wersję po analizie AI albo wgraną świadomie bez niej.
  const shareReady =
    review.status === "done" ||
    review.status === "done_partial" ||
    review.status === "skipped";
  const state = review.review_state;
  const isReviewer = review.is_my_review;
  const canShare =
    review.is_author && shareReady && (state === "draft" || state === "returned");
  const decided =
    state === "accepted" ||
    state === "accepted_conditional" ||
    state === "returned";

  return (
    <div className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-[var(--color-text-primary)]">
          Recenzja
        </span>
        <Badge color={WORKFLOW_STATE_COLOR[state]}>
          {WORKFLOW_STATE_LABEL[state]}
        </Badge>
        {review.assignments.length > 0 && (
          <span className="text-xs text-[var(--color-text-muted)]">
            recenzenci:{" "}
            {review.assignments.map((a) => a.reviewer_name).join(", ")}
          </span>
        )}
      </div>

      {decided && (
        <div className="text-sm text-[var(--color-text-secondary)]">
          Decyzja: <b>{review.review_state_display}</b>
          {review.decided_by_name ? ` — ${review.decided_by_name}` : ""}
          {review.decided_at
            ? ` · ${new Date(review.decided_at).toLocaleString("pl-PL")}`
            : ""}
          {review.decision_note && (
            <p className="mt-1 whitespace-pre-wrap border-l-2 border-[var(--color-border-strong)] pl-2 text-[var(--color-text-primary)]">
              {review.decision_note}
            </p>
          )}
        </div>
      )}

      {!shareReady && !decided && (
        <p className="text-xs text-[var(--color-text-muted)]">
          Najpierw uruchom analizę AI, potem udostępnisz recenzję dyrektorom
          technicznym.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {canShare && (
          <Button size="sm" onClick={() => setShareOpen(true)}>
            <Send size={13} className="mr-1.5" />
            {state === "returned"
              ? "Udostępnij ponownie"
              : "Udostępnij recenzentom"}
          </Button>
        )}
        {isReviewer && state === "in_review" && (
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setDecision("accepted")}
            >
              <CheckCircle2 size={13} className="mr-1.5" /> Zaakceptuj
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setDecision("accepted_conditional")}
            >
              <Check size={13} className="mr-1.5" /> Zaakceptuj warunkowo
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => setDecision("returned")}
            >
              <Undo2 size={13} className="mr-1.5" /> Zakończ i zwróć
            </Button>
          </>
        )}
      </div>

      {shareOpen && (
        <ShareModal review={review} onClose={() => setShareOpen(false)} />
      )}
      {decision && (
        <DecisionModal
          review={review}
          decision={decision}
          onClose={() => setDecision(null)}
        />
      )}
    </div>
  );
}

export function EngAronReviewDetailPage() {
  const { reviewId } = useParams<{ reviewId: string }>();
  const review = useReview(reviewId);
  const task = useTaskPolling(
    review.data && ["queued", "running"].includes(review.data.status)
      ? review.data.async_task_id
      : null,
  );
  const findings = useReviewFindings(reviewId, review.data?.status);
  const [emailOpen, setEmailOpen] = useState(false);
  const [newVerOpen, setNewVerOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const emailText = useReviewEmailText(reviewId, emailOpen);
  const startReview = useStartReview();
  const cancelReview = useCancelReview();
  const [copied, setCopied] = useState(false);

  const active =
    review.data && ["queued", "running"].includes(review.data.status);

  const logTail = useMemo(() => {
    const log = task.data?.log ?? "";
    return log.split("\n").slice(-14).join("\n");
  }, [task.data?.log]);

  const progress = useMemo(
    () => reviewProgress(task.data?.log ?? "", review.data?.status ?? "queued"),
    [task.data?.log, review.data?.status],
  );

  if (review.isLoading || !review.data) return <PageLoader />;
  const r = review.data;

  const allFindings = findings.data ?? [];
  // „Poprawione" (przeniesione z poprzedniej wersji) wędrują na sam dół, osobno
  // i bez akcji; reszta zostaje w głównej liście uwag.
  const activeFindings = allFindings.filter((f) => f.status !== "fixed");
  const fixedFindings = allFindings.filter((f) => f.status === "fixed");

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      <Link
        to="/eng-aron"
        className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
      >
        <ArrowLeft size={14} /> Recenzje
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
            {r.label}{" "}
            <span className="text-[var(--color-text-muted)]">v{r.version}</span>
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            {r.project_name ?? "bez przedsięwzięcia"} ·{" "}
            {DOC_TYPE_LABEL[r.doc_type]} ·{" "}
            {r.page_count ? `${r.page_count} str. · ` : ""}
            {r.file.url ? (
              <a
                href={r.file.url}
                className="underline"
                target="_blank"
                rel="noreferrer"
              >
                {r.file.original_filename}
              </a>
            ) : (
              r.file.original_filename
            )}
            {r.prior_version ? ` · powstała z v${r.prior_version}` : ""}
          </p>
          {/* Czym i kiedy zrobiona */}
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            Tryb:{" "}
            {REVIEW_MODES.find((m) => m.value === r.mode)?.label ?? r.mode}
            {r.models_used ? ` · modele: ${r.models_used}` : ""}
            {r.finished_at
              ? ` · zakończono ${new Date(r.finished_at).toLocaleString("pl-PL")}`
              : r.created_at
                ? ` · wgrano ${new Date(r.created_at).toLocaleString("pl-PL")}`
                : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge color={REVIEW_STATUS_COLOR[r.status]}>
            {REVIEW_STATUS_LABEL[r.status]}
          </Badge>
          {r.review_state !== "draft" && (
            <Badge color={WORKFLOW_STATE_COLOR[r.review_state]}>
              {WORKFLOW_STATE_LABEL[r.review_state]}
            </Badge>
          )}
          {r.cost_usd !== "0.000000" && (
            <span className="text-xs text-[var(--color-text-muted)]">
              koszt AI: ${Number(r.cost_usd).toFixed(2)}
            </span>
          )}
          {active ? (
            <Button
              size="sm"
              variant="danger"
              loading={cancelReview.isPending}
              onClick={() => cancelReview.mutate(r.id)}
            >
              Anuluj analizę
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              loading={startReview.isPending}
              onClick={() => startReview.mutate(r.id)}
            >
              {r.status === "uploaded" || r.status === "skipped"
                ? "Analizuj"
                : "Analizuj ponownie"}
            </Button>
          )}
          {(r.findings_count > 0 || r.summary_text) && (
            <Button size="sm" onClick={() => setEmailOpen(true)}>
              <ClipboardCopy size={14} className="mr-1.5" /> Treść e-maila
            </Button>
          )}
          {!active &&
            (r.status === "done" ||
              r.status === "done_partial" ||
              r.status === "skipped") && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setNewVerOpen(true)}
            >
              <FilePlus2 size={14} className="mr-1.5" /> Nowa wersja
            </Button>
          )}
        </div>
      </div>

      {r.status === "error" && r.error && (
        <div className="border border-red-300 bg-red-50 text-red-700 text-sm p-3">
          {r.error}
        </div>
      )}

      {r.map_quality && typeof r.map_quality.score === "number" && (
        <div className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[var(--color-text-primary)]">
              Czytelność map końcowych
            </span>
            <Badge
              color={
                r.map_quality.score >= 7
                  ? "green"
                  : r.map_quality.score >= 4
                    ? "yellow"
                    : "red"
              }
            >
              {r.map_quality.score}/10
            </Badge>
            <span className="text-xs text-[var(--color-text-muted)]">
              (ocena subiektywna)
            </span>
          </div>
          {r.map_quality.rationale && (
            <p className="text-sm text-[var(--color-text-secondary)]">
              {r.map_quality.rationale}
            </p>
          )}
          {r.map_quality.recommendation && (
            <p className="text-sm text-[var(--color-text-primary)]">
              <span className="text-[var(--color-text-muted)]">
                Rekomendacja:
              </span>{" "}
              {r.map_quality.recommendation}
            </p>
          )}
          {r.map_quality.criteria.length > 0 && (
            <details className="text-xs text-[var(--color-text-muted)]">
              <summary className="cursor-pointer">Kryteria</summary>
              <ul className="mt-1 space-y-0.5">
                {r.map_quality.criteria.map((c, i) => (
                  <li key={i}>
                    {c.name}: {c.score ?? "—"}/10{c.note ? ` — ${c.note}` : ""}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {active && (
        <div className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3 space-y-2">
          {progress && (
            <div className="space-y-1">
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="text-[var(--color-text-secondary)] truncate">
                  {progress.label}
                </span>
                <span className="font-mono font-semibold text-[var(--color-text-primary)]">
                  {progress.pct}%
                </span>
              </div>
              <div className="h-2 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] overflow-hidden">
                <div
                  className="h-full bg-[var(--color-primary)] transition-[width] duration-700 ease-out"
                  style={{ width: `${progress.pct}%` }}
                />
              </div>
              <p className="text-[11px] text-[var(--color-text-muted)]">
                Czas etapów jest szacowany
              </p>
            </div>
          )}
          <div>
            <p className="text-xs text-[var(--color-text-muted)] mb-1">
              Postęp analizy (odświeża się automatycznie):
            </p>
            <pre className="text-xs whitespace-pre-wrap max-h-56 overflow-y-auto text-[var(--color-text-secondary)]">
              {logTail || "Czekam na uruchomienie zadania…"}
            </pre>
          </div>
        </div>
      )}

      {!active && r.status !== "uploaded" && <WorkflowPanel review={r} />}

      {!active && r.status !== "uploaded" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-[var(--color-text-primary)]">
              Uwagi ({activeFindings.length})
            </h2>
            {(r.is_author || r.is_my_review) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setManualOpen(true)}
              >
                <Plus size={13} className="mr-1" /> Dodaj uwagę ręcznie
              </Button>
            )}
          </div>
          {activeFindings.length > 0 ? (
            activeFindings.map((f) => <FindingCard key={f.id} finding={f} />)
          ) : findings.isLoading ? null : (
            <div className="border border-dashed border-[var(--color-border-strong)] p-8 text-center text-sm text-[var(--color-text-secondary)]">
              Brak uwag — projekt do akceptacji.
            </div>
          )}

          {fixedFindings.length > 0 && (
            <div className="space-y-2 pt-3 mt-1 border-t border-[var(--color-border)]">
              <h3 className="text-sm font-medium text-[var(--color-text-primary)] inline-flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-green-600" />
                Poprawione ({fixedFindings.length})
              </h3>
              <p className="text-xs text-[var(--color-text-muted)]">
                Uwagi z poprzedniej wersji, których ENG-ARON już nie stwierdził —
                informacyjnie, bez akcji.
              </p>
              {fixedFindings.map((f) => (
                <FindingCard key={f.id} finding={f} />
              ))}
            </div>
          )}
        </div>
      )}

      <Modal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        title="Treść e-maila z uwagami"
        size="lg"
      >
        <div className="space-y-3">
          <pre className="text-sm whitespace-pre-wrap bg-[var(--color-bg-elevated)] border border-[var(--color-border)] p-3 max-h-[55vh] overflow-y-auto">
            {emailText.data?.text ?? "…"}
          </pre>
          <div className="flex justify-end">
            <Button
              onClick={async () => {
                await navigator.clipboard.writeText(emailText.data?.text ?? "");
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? "Skopiowano ✓" : "Kopiuj do schowka"}
            </Button>
          </div>
        </div>
      </Modal>

      {newVerOpen && (
        <NewVersionModal review={r} onClose={() => setNewVerOpen(false)} />
      )}
      {manualOpen && (
        <ManualFindingModal review={r} onClose={() => setManualOpen(false)} />
      )}
    </div>
  );
}
